import { clearMediaDiagnosticHistory } from '@/services/diagnostics/mediaDiagnosticRuntime';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Image, StyleSheet } from 'react-native';
import * as SplashScreen from 'expo-splash-screen';
import { getSession, supabase } from '../services/supabase';
import { userActions, globalState } from '../state/globalState';
import { InitializeLogger, SupabaseLogger } from '../utils/logger';
import theme from '../config/theme';
import { initNetworkMonitoring } from '../services/network';
import { databaseService } from '../services/databaseService';
import { debugPause } from '../utils/debugPause';
import type { Session } from '@supabase/supabase-js';
import { APP_CONSTANTS } from '../config/constants';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '../config/recoveryDiagnostic';
// Logger específico para o inicializador
const initLogger = InitializeLogger.App;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Manter a splash screen visível enquanto fazemos a inicialização
SplashScreen.preventAutoHideAsync().catch(err => 
  initLogger.warn('Erro ao prevenir o auto hide da splash screen:', err)
);

interface AppInitializerProps {
  children: React.ReactNode;
}

const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const [appIsReady, setAppIsReady] = useState(false);
  const sessionProcessingRef = useRef(false);
  const lastProcessedUserRef = useRef<string | null>(null);

  const normalizarErro = useCallback((erro: unknown): string => {
    if (erro instanceof Error) {
      return erro.message;
    }
    if (typeof erro === 'string') {
      return erro;
    }
    try {
      return JSON.stringify(erro);
    } catch {
      return 'erro desconhecido';
    }
  }, []);

  type ProcessSessionOptions = {
    session: Session;
    resumo?: string[];
    source: string;
  };

  const waitForDirectSignupBootstrapIfNeeded = useCallback(async (source: string) => {
    if (!globalState.user.directSignup.get().isBootstrapping) {
      return;
    }
    initLogger.info(`[${source}] Aguardando bootstrap direto concluir antes de continuar a inicialização`);
    let attempts = 0;
    while (globalState.user.directSignup.get().isBootstrapping) {
      attempts += 1;
      await sleep(300);
    }
    initLogger.info(`[${source}] Bootstrap direto concluído após ${attempts} ciclos de espera`);
  }, []);

  const processAuthenticatedSession = useCallback(
    async ({ session, resumo, source }: ProcessSessionOptions) => {
      const userId = session.user?.id;

      if (!userId) {
        initLogger.warn(`[${source}] Sessão recebida sem userId. Ignorando processamento.`);
        return;
      }

      await waitForDirectSignupBootstrapIfNeeded(source);

      if (sessionProcessingRef.current) {
        initLogger.info(`[${source}] Já existe um processamento de sessão em andamento. Ignorando chamada paralela.`);
        return;
      }

      if (
        lastProcessedUserRef.current === userId &&
        globalState.user.checkUserDatabaseDone.get()
      ) {
        initLogger.info(`[${source}] Sessão do usuário ${userId} já processada anteriormente. Pulando etapa.`);
        userActions.setSession(session);
        return;
      }

      sessionProcessingRef.current = true;
      globalState.user.checkUserDatabaseDone.set(false);

      try {
        initLogger.info(`[${source}] Processando sessão autenticada para o usuário ${userId}`);
        userActions.setSession(session);
        resumo?.push(`Sessão: ativa (${userId})`);

        if (!IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
          try {
            await initNetworkMonitoring();
            resumo?.push('Rede: monitoramento ativo');
          } catch (networkError) {
            initLogger.error(`[${source}] Erro ao iniciar monitoramento de rede:`, networkError);
            resumo?.push(`Rede: erro (${normalizarErro(networkError)})`);
          }
        }

        try {
          await databaseService.initialize(userId);
          resumo?.push('Banco local: pronto');
        } catch (dbError) {
          initLogger.error(`[${source}] Erro ao inicializar banco local:`, dbError);
          resumo?.push(`Banco local: erro (${normalizarErro(dbError)})`);
          throw dbError;
        }

        try {
          if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
            const localUser = await databaseService.readItem<Record<string, unknown>>(
              APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
              userId
            );
            userActions.setUserExistsInDatabase(Boolean(localUser));
            if (localUser) {
              userActions.setProfile(localUser as any);
            }
            resumo?.push(`Usuário local: ${localUser ? 'presente' : 'inexistente'}`);
          } else {
            const { checkUserExists } = require('../services/authService') as typeof import('../services/authService');
            const userCheck = await checkUserExists(userId);
            userActions.setUserExistsInDatabase(userCheck.exists);
            resumo?.push(`Usuário no banco: ${userCheck.exists ? 'presente' : 'inexistente'}`);
          }
        } catch (checkError) {
          initLogger.error(`[${source}] Erro ao verificar existência local do usuário:`, checkError);
          resumo?.push(`Usuário local: erro (${normalizarErro(checkError)})`);
        }
      } catch (error) {
        initLogger.error(`[${source}] Erro durante processamento da sessão autenticada:`, error);
      } finally {
        globalState.user.checkUserDatabaseDone.set(true);
        sessionProcessingRef.current = false;
        lastProcessedUserRef.current = userId;
      }
    },
    [normalizarErro, waitForDirectSignupBootstrapIfNeeded]
  );

  useEffect(() => {
    async function initialize() {
      const resumoInicializacao: string[] = [];
      const registrarResumo = () => {
        if (resumoInicializacao.length === 0) {
          return;
        }
        initLogger.info(
          `Resumo da inicialização (${resumoInicializacao.length} etapas): ${resumoInicializacao.join(' | ')}`
        );
      };

      try {
        initLogger.info('[init] Iniciando processo de inicialização do app');

        if (!IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
          SupabaseLogger.logAuthEnvironment();
          resumoInicializacao.push('Supabase: cliente pronto');
        } else {
          resumoInicializacao.push('Recuperação: sessão e banco somente locais');
        }

        userActions.setLoading(true);

        const session = await getSession();

        if (session) {
          await processAuthenticatedSession({
            session,
            resumo: resumoInicializacao,
            source: 'Init#getSession'
          });
        } else {
          resumoInicializacao.push('Sessão: não encontrada');
          userActions.clearUser();

          if (!IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
            try {
              await initNetworkMonitoring();
              resumoInicializacao.push('Rede: monitoramento ativo');
            } catch (networkError) {
              initLogger.error('[Init] Erro ao inicializar monitoramento de rede:', networkError);
              resumoInicializacao.push(`Rede: erro (${normalizarErro(networkError)})`);
            }
          }

          try {
            await databaseService.initialize();
            resumoInicializacao.push('Banco local: pronto (modo visitante)');
          } catch (dbError) {
            initLogger.error('[Init] Erro ao inicializar banco de dados local:', dbError);
            resumoInicializacao.push(`Banco local: erro (${normalizarErro(dbError)})`);
          }

          globalState.user.checkUserDatabaseDone.set(true);
        }

        resumoInicializacao.push('Inicialização: concluída');
      } catch (error) {
        initLogger.error('Erro durante a inicialização:', error);
        resumoInicializacao.push(`Inicialização: falhou (${normalizarErro(error)})`);
        userActions.setError('Erro durante a inicialização do app');
      } finally {
        registrarResumo();
        userActions.setLoading(false);
        setAppIsReady(true);
      }
    }

    initialize();
  }, []);

  useEffect(() => {
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      return;
    }

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      initLogger.info(`[AuthListener] Evento recebido: ${event}`);

      if (session) {
        if (!globalState.user.checkUserDatabaseDone.get()) {
          userActions.setLoading(true);
          processAuthenticatedSession({
            session,
            source: `AuthListener#${event}`,
          })
            .catch(error => {
              initLogger.error(`[AuthListener] Erro ao processar sessão após evento ${event}:`, error);
            })
            .finally(() => {
              userActions.setLoading(false);
            });
        } else {
          initLogger.info('[AuthListener] Sessão já processada, apenas atualizando estado.');
          userActions.setSession(session);
        }
      } else if (event === 'SIGNED_OUT') {
        clearMediaDiagnosticHistory();
        initLogger.info('[AuthListener] Evento SIGNED_OUT detectado, limpando usuário.');
        userActions.clearUser();
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
      initLogger.debug('AuthListener removido na desmontagem do AppInitializer');
    };
  }, [processAuthenticatedSession]);

  // Efeito para esconder a splash screen quando estiver pronto
  useEffect(() => {
    if (appIsReady) {
      initLogger.debug('App pronto, escondendo splash screen');
      SplashScreen.hideAsync().catch(err => 
        initLogger.warn('Erro ao esconder splash screen:', err)
      );
    }
  }, [appIsReady]);

  if (!appIsReady) {
    return (
      <View style={styles.container}>
        <Image 
          source={require('../assets/splash-icon.png')}
          style={styles.splashImage}
          resizeMode="contain"
        />
      </View>
    );
  }

  return <>{children}</>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  splashImage: {
    width: 150,
    height: 150,
  },
});

export default AppInitializer; 
